/* RoadmapLayer — six interactive roadmap-layer experiences for role previews.
   Gateway + overlay reuse the exact "Oka Roju — Live One Day" pattern (dark
   shell, same open/close/exit interaction). Inside, each layer is an
   interactive activity driven ONLY by the supplied data: one active item at a
   time, previous/next navigation, progressive disclosure. Content is rendered
   verbatim — nothing added, renamed, reordered or dropped. */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { SubjectUnderstanding } from '../subject/SubjectUnderstanding';
import { X, ChevronDown, ChevronUp, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PARCHMENT = '#F5E9D4';
const DEEP = '#0B1F2E';
const PANEL = '#1A3548';
const SERIF = '"Source Serif 4", "Iowan Old Style", "Palatino Linotype", Georgia, serif';

const LAYER_CACHE = new Map(); // session cache: `${itemId}:${layerNo}` → payload

const bodyStyle = { color: `${PARCHMENT}DD`, fontSize: 15 };
const dimStyle = { color: `${PARCHMENT}88`, fontSize: 13.5 };
const COLOURS = { green: '#34D399', amber: '#FBBF24', red: '#F87171' };

/* ── shared primitives ── */
const Pill = ({ children, onClick, testId, subtle = false, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-full px-6 font-semibold uppercase tracking-[0.14em] transition-transform hover:scale-[1.02] disabled:opacity-30 disabled:hover:scale-100"
    style={{
      background: subtle ? 'transparent' : PARCHMENT,
      color: subtle ? PARCHMENT : DEEP,
      border: subtle ? `1px solid ${PARCHMENT}66` : 'none',
      minHeight: 46, fontSize: 13,
    }}
    data-testid={testId}
  >{children}</button>
);

const Row = ({ label, text }) => {
  if (text === undefined || text === null || text === '') return null;
  return (
    <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>
      <span className="font-bold" style={{ color: PARCHMENT }}>{label}: </span>{String(text)}
    </p>
  );
};

const Bullets = ({ items }) => {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <ul className="space-y-1.5">
      {items.map((x, i) => (
        <li key={i} className="flex gap-2 leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>
          <span style={{ color: `${PARCHMENT}66` }}>·</span>
          <span className="min-w-0">{String(x)}</span>
        </li>
      ))}
    </ul>
  );
};

const Card = ({ children, tint }) => (
  <div className="rounded-2xl px-4 py-3.5 space-y-2"
    style={{ background: PANEL, border: `1px solid ${tint || `${PARCHMENT}33`}` }}>
    {children}
  </div>
);

const Chip = ({ children, colour }) => (
  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12.5px] font-semibold break-words max-w-full"
    style={{ border: `1px solid ${colour || `${PARCHMENT}55`}`, color: colour || `${PARCHMENT}CC` }}>
    {children}
  </span>
);

/* collapsed disclosure — used for sources and long supplementary blocks */
const Disclosure = ({ label, count, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} data-testid={testId}
        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-bold uppercase tracking-widest"
        style={{ border: `1px solid ${PARCHMENT}44`, color: `${PARCHMENT}AA`, minHeight: 40 }}>
        {label}{count !== undefined ? ` (${count})` : ''} {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>
      {open && <div className="mt-3 space-y-2.5">{children}</div>}
    </div>
  );
};

const A = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    className="underline underline-offset-2 break-all" style={{ color: PARCHMENT }}>
    {children}
  </a>
);

/* Sources — resolves `evidence:` refs via the payload's evidence_refs; keeps
   resource objects and plain refs verbatim, collapsed until requested. */
const Sources = ({ srcs = [], resources = [], basisList = [], evidenceMap = {}, testId }) => {
  const evIds = [];
  const plainRefs = [];
  (srcs || []).forEach((s) => {
    const m = typeof s === 'string' && s.match(/^evidence:(.+)$/);
    if (m && evidenceMap[m[1]]) evIds.push(m[1]);
    else plainRefs.push(String(s));
  });
  const count = evIds.length + plainRefs.length + (resources?.length || 0) + (basisList?.length || 0);
  if (!count) return null;
  return (
    <Disclosure label="Sources" count={count} testId={testId}>
      {(resources || []).map((r, i) => (
        <div key={`r${i}`} className="rounded-xl px-3 py-2.5 space-y-1" style={{ border: `1px solid ${PARCHMENT}22` }}>
          <p className="font-semibold leading-snug break-words" style={{ ...bodyStyle, color: PARCHMENT }}>{r.title}</p>
          {r.url && <p className="break-all" style={dimStyle}><A href={r.url}>{r.url}</A></p>}
          <p style={dimStyle}>
            {[r.type, r.cost, r.hours !== undefined ? `${r.hours}h` : null, r.language,
              r.last_verified ? `verified ${r.last_verified}` : null].filter(Boolean).join(' · ')}
          </p>
          {r.why_this_one && <p className="leading-relaxed break-words" style={dimStyle}>{r.why_this_one}</p>}
        </div>
      ))}
      {(basisList || []).map((b, i) => (
        <div key={`b${i}`} className="rounded-xl px-3 py-2.5 space-y-1" style={{ border: `1px solid ${PARCHMENT}22` }}>
          {b.claim && <p className="leading-relaxed break-words" style={bodyStyle}>{b.claim}</p>}
          {b.source_url && <p className="break-all" style={dimStyle}><A href={b.source_url}>{b.source_url}</A></p>}
          <p style={dimStyle}>{[b.publisher, b.source_date, b.source_type, b.claim_status].filter(Boolean).join(' · ')}</p>
        </div>
      ))}
      {evIds.map((id) => {
        const e = evidenceMap[id];
        return (
          <div key={id} className="rounded-xl px-3 py-2.5 space-y-1" style={{ border: `1px solid ${PARCHMENT}22` }}>
            {e.claim && <p className="leading-relaxed break-words" style={bodyStyle}>{e.claim}</p>}
            {e.source_url && <p className="break-all" style={dimStyle}><A href={e.source_url}>{e.source_url}</A></p>}
            <p style={dimStyle}>{[e.publisher, e.source_date, e.source_type, e.claim_status].filter(Boolean).join(' · ')}</p>
          </div>
        );
      })}
      {plainRefs.map((s, i) => (
        <p key={`p${i}`} className="break-all" style={dimStyle}>{s}</p>
      ))}
    </Disclosure>
  );
};

/* progress indicator + prev/next — same visual language as the OkaRoju dots */
const StepNav = ({ idx, total, onPrev, onNext, layerNo, itemWord = 'item' }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap">
    <Pill subtle onClick={onPrev} disabled={idx <= 0} testId={`layer-prev-${layerNo}`}>Previous</Pill>
    <span className="text-[12.5px] font-bold uppercase tracking-[0.2em]" style={{ color: `${PARCHMENT}77` }}
      data-testid={`layer-progress-${layerNo}`}>
      {itemWord} {idx + 1} / {total}
    </span>
    <Pill onClick={onNext} disabled={idx >= total - 1} testId={`layer-next-${layerNo}`}>Next</Pill>
  </div>
);

const Dots = ({ idx, total }) => (
  <div className="flex items-center gap-2 flex-wrap">
    {Array.from({ length: total }).map((_, d) => (
      <span key={d} className="rounded-full transition-all duration-500"
        style={{ width: d === idx ? 22 : 8, height: 8, background: d <= idx ? PARCHMENT : `${PARCHMENT}33` }} />
    ))}
  </div>
);

/* [Spot it] — supplied prompt + options; tap to commit, then the supplied
   answer and why are revealed (data unchanged, interaction only) */
const SpotIt = ({ spot, layerNo }) => {
  const [picked, setPicked] = useState(null);
  if (!spot?.prompt || !Array.isArray(spot.options)) return null;
  return (
    <div className="space-y-2" data-testid={`layer-spotit-${layerNo}`}>
      <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>
        <span className="font-bold" style={{ color: PARCHMENT }}>[Spot it]</span> {spot.prompt}
      </p>
      <div className="space-y-2">
        {spot.options.map((opt, i) => {
          const committed = picked !== null;
          const isCorrect = committed && i === spot.answer_index;
          const isWrong = committed && picked === i && i !== spot.answer_index;
          return (
            <button key={i} type="button" disabled={committed}
              onClick={() => setPicked(i)}
              className="w-full text-left rounded-xl px-4 py-3 transition-all break-words [overflow-wrap:anywhere]"
              style={{
                minHeight: 46,
                background: isCorrect ? `${COLOURS.green}22` : isWrong ? `${COLOURS.red}22` : `${PARCHMENT}0D`,
                border: `1px solid ${isCorrect ? COLOURS.green : isWrong ? COLOURS.red : `${PARCHMENT}33`}`,
                color: PARCHMENT, fontSize: 14.5,
                opacity: committed && !isCorrect && !isWrong ? 0.45 : 1,
              }}
              data-testid={`layer-spotit-opt-${i}`}
            >{opt}</button>
          );
        })}
      </div>
      {picked !== null && spot.why && (
        <p className="leading-relaxed break-words rounded-xl px-4 py-3" style={{ ...bodyStyle, borderLeft: `3px solid ${PARCHMENT}` , background: `${PARCHMENT}10` }}>
          {spot.why}
        </p>
      )}
    </div>
  );
};

/* ══ LAYER 1 · SUBJECTS — selectable subject pathway ══ */
const Layer1 = ({ data, evidenceMap }) => {
  const modules = data.modules || [];
  const [sel, setSel] = useState(0); // 0..modules-1, then modules => EXIT DOOR
  const hasExit = !!data.exit_door;
  const total = modules.length + (hasExit ? 1 : 0);
  const isExit = hasExit && sel === modules.length;
  const m = !isExit ? modules[sel] : null;
  const s = m?.subject || {};
  const ed = data.exit_door || {};
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2" data-testid="layer1-subject-list">
        {modules.map((mod, i) => (
          <button key={mod.n} type="button" onClick={() => setSel(i)}
            className="rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors break-words max-w-full"
            style={{
              minHeight: 40,
              background: sel === i ? PARCHMENT : `${PARCHMENT}0D`,
              color: sel === i ? DEEP : `${PARCHMENT}CC`,
              border: `1px solid ${sel === i ? PARCHMENT : `${PARCHMENT}33`}`,
            }}
            data-testid={`layer1-subject-${mod.n}`}>
            {mod.n}. {mod.subject?.name || mod.subject_id}
          </button>
        ))}
        {hasExit && (
          <button type="button" onClick={() => setSel(modules.length)}
            className="rounded-full px-3.5 py-2 text-[12px] font-bold uppercase tracking-wider transition-colors"
            style={{
              minHeight: 40,
              background: isExit ? PARCHMENT : `${PARCHMENT}0D`,
              color: isExit ? DEEP : `${PARCHMENT}CC`,
              border: `1px dashed ${isExit ? PARCHMENT : `${PARCHMENT}55`}`,
            }}
            data-testid="layer1-exit-door">
            Exit door
          </button>
        )}
      </div>

      {m && (
        <Card>
          <p className="font-bold leading-snug break-words" style={{ color: PARCHMENT, fontSize: 16.5 }}>
            {m.n}. {s.name || m.subject_id}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {m.band !== undefined && <Chip>{String(m.band).toUpperCase()}</Chip>}
            {m.depth_needed && <Chip>depth: {m.depth_needed}</Chip>}
            {s.permanence_grade !== undefined && <Chip>grade {s.permanence_grade}</Chip>}
            {m.hours_for_this_depth !== undefined && <Chip>{m.hours_for_this_depth}h</Chip>}
          </div>
          <Row label="What it is" text={s.what_it_is} />
          <Row label="Permanent because" text={s.why_permanent} />
          <Row label="This role needs it" text={m.why_this_role_needs_it} />
          <Row label="Skip it and" text={s.agent_blindness} />
          <Row label="Common trap" text={s.common_trap} />
          <Row label="Ready to move on" text={s.ready_to_move_on} />
          <Row label="You already have this if" text={s.already_have_this_if} />
          {Array.isArray(s.prerequisites) && s.prerequisites.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-bold" style={{ ...bodyStyle, color: PARCHMENT }}>Prerequisites:</span>
              {s.prerequisites.map((p, i) => <Chip key={i}>{String(p)}</Chip>)}
            </div>
          )}
          <Sources srcs={s.src} resources={s.resources} evidenceMap={evidenceMap} testId="layer1-sources" />
        </Card>
      )}

      {isExit && (
        <Card>
          <p className="font-bold leading-snug" style={{ color: PARCHMENT, fontSize: 16.5 }}>EXIT DOOR</p>
          <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{ed.statement}</p>
          {ed.free_and_government_routes_first && (
            <>
              <p className="font-bold pt-1" style={{ ...bodyStyle, color: PARCHMENT }}>Free and government routes first</p>
              {Array.isArray(ed.free_and_government_routes_first)
                ? <Bullets items={ed.free_and_government_routes_first} />
                : <p className="leading-relaxed break-words" style={bodyStyle}>{String(ed.free_and_government_routes_first)}</p>}
            </>
          )}
          {ed.what_to_look_for && (
            <>
              <p className="font-bold pt-1" style={{ ...bodyStyle, color: PARCHMENT }}>What to look for</p>
              {Array.isArray(ed.what_to_look_for) ? <Bullets items={ed.what_to_look_for} />
                : <p className="leading-relaxed break-words" style={bodyStyle}>{String(ed.what_to_look_for)}</p>}
            </>
          )}
          {ed.what_to_avoid && (
            <>
              <p className="font-bold pt-1" style={{ ...bodyStyle, color: PARCHMENT }}>What to avoid</p>
              {Array.isArray(ed.what_to_avoid) ? <Bullets items={ed.what_to_avoid} />
                : <p className="leading-relaxed break-words" style={bodyStyle}>{String(ed.what_to_avoid)}</p>}
            </>
          )}
          {ed.questions_to_ask_before_you_pay && (
            <>
              <p className="font-bold pt-1" style={{ ...bodyStyle, color: PARCHMENT }}>Questions to ask before you pay</p>
              {Array.isArray(ed.questions_to_ask_before_you_pay) ? <Bullets items={ed.questions_to_ask_before_you_pay} />
                : <p className="leading-relaxed break-words" style={bodyStyle}>{String(ed.questions_to_ask_before_you_pay)}</p>}
            </>
          )}
          <Row label="Degree route" text={typeof ed.degree_route === 'string' ? ed.degree_route : undefined} />
          {typeof ed.degree_route === 'object' && ed.degree_route !== null && (
            <div className="space-y-1.5">
              <p className="font-bold" style={{ ...bodyStyle, color: PARCHMENT }}>Degree route</p>
              {Object.values(ed.degree_route).map((v, i) => (
                <p key={i} className="leading-relaxed break-words" style={bodyStyle}>{String(v)}</p>
              ))}
            </div>
          )}
          <Sources srcs={ed.src} evidenceMap={evidenceMap} testId="layer1-exit-sources" />
        </Card>
      )}

      <StepNav idx={sel} total={total} layerNo={1} itemWord="Subject"
        onPrev={() => setSel(Math.max(0, sel - 1))}
        onNext={() => setSel(Math.min(total - 1, sel + 1))} />

      {Array.isArray(data.assumed_prior) && data.assumed_prior.length > 0 && (
        <Disclosure label="Assumed prior" count={data.assumed_prior.length} testId="layer1-assumed-prior">
          {data.assumed_prior.map((a, i) => (
            <div key={i} className="rounded-xl px-3 py-2.5 space-y-1" style={{ border: `1px solid ${PARCHMENT}22` }}>
              <p className="font-semibold break-words" style={{ ...bodyStyle, color: PARCHMENT }}>{a.subject_id}</p>
              {a.reason && <p className="leading-relaxed break-words" style={dimStyle}>{a.reason}</p>}
            </div>
          ))}
        </Disclosure>
      )}
      {data.band_totals && (
        <p className="leading-relaxed break-words" style={dimStyle}>
          {Object.entries(data.band_totals).map(([k, v]) =>
            `${k}: ${v.module_count} modules · ${v.hours}h`).join('  ·  ')}
        </p>
      )}
      {Array.isArray(data.honesty_notes) && data.honesty_notes.length > 0 && (
        <Disclosure label="Honesty notes" count={data.honesty_notes.length} testId="layer1-honesty">
          <Bullets items={data.honesty_notes} />
        </Disclosure>
      )}
    </div>
  );
};

/* ══ LAYERS 2 & 3 — guided step-by-step node sequence ══ */
const NodeStepLayer = ({ data, layerNo, evidenceMap }) => {
  const nodes = data.nodes || [];
  const [idx, setIdx] = useState(0);
  const n = nodes[idx] || {};
  const dl = n.duration_by_lane || {};
  const dep = n.depth || {};
  const td = data.total_duration || {};
  const twc = data.total_with_capstone || {};
  const ba = data.branch_adds || {};
  const laneLine = (obj) => ['student', 'switcher', 'working_pro']
    .filter((k) => obj[k]).map((k) => `${k} ${obj[k]}`).join(' · ');
  const basisList = [n.recommendation_basis, n.ai_exposure_basis, n.sign_off_basis]
    .filter((b) => b && typeof b === 'object' && (b.claim || b.source_url));
  return (
    <div className="space-y-5">
      {(data.title || data.subtitle) && (
        <div>
          {data.title && <p className="font-bold break-words" style={{ color: PARCHMENT, fontSize: 16 }}>{data.title}</p>}
          {data.subtitle && <p className="leading-relaxed break-words" style={dimStyle}>{data.subtitle}</p>}
        </div>
      )}
      {laneLine(td) && <p style={dimStyle}><span className="font-bold" style={{ color: `${PARCHMENT}AA` }}>total </span>{laneLine(td)}</p>}
      {laneLine(twc) && <p style={dimStyle}><span className="font-bold" style={{ color: `${PARCHMENT}AA` }}>with capstone </span>{laneLine(twc)}</p>}
      {laneLine(ba) && <p style={dimStyle}><span className="font-bold" style={{ color: `${PARCHMENT}AA` }}>branch adds </span>{laneLine(ba)}</p>}

      <Dots idx={idx} total={nodes.length} />

      <Card>
        <p className="font-bold leading-snug break-words" style={{ color: PARCHMENT, fontSize: 16.5 }}>
          {n.n}. {n.title}{n.subtitle ? ` — ${n.subtitle}` : ''}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {dl.student && <Chip>student {dl.student}</Chip>}
          {dl.switcher && <Chip>switcher {dl.switcher}</Chip>}
          {dl.working_pro && <Chip>working {dl.working_pro}</Chip>}
          {n.ai_exposure && <Chip>AI exposure: {n.ai_exposure_display_label || n.ai_exposure}</Chip>}
          {n.sign_off && <Chip>sign-off: {n.sign_off}</Chip>}
          {n.recommendation && <Chip>{n.recommendation}</Chip>}
          {n.unverified === true && <Chip colour={COLOURS.amber}>unverified</Chip>}
        </div>
        {n.ai_exposure_note && <p className="leading-relaxed break-words" style={dimStyle}>{n.ai_exposure_note}</p>}
        <Bullets items={n.detail} />
        {n.tracks?.agent_handles?.length > 0 && (
          <div className="rounded-xl px-3.5 py-3 space-y-1.5" style={{ border: `1px solid #38BDF877`, background: '#38BDF811' }}>
            <p className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: '#7DD3FC' }}>Agent handles</p>
            <Bullets items={n.tracks.agent_handles} />
          </div>
        )}
        {n.tracks?.you_own?.length > 0 && (
          <div className="rounded-xl px-3.5 py-3 space-y-1.5" style={{ border: `1px solid ${COLOURS.green}77`, background: `${COLOURS.green}11` }}>
            <p className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: COLOURS.green }}>You own</p>
            <Bullets items={n.tracks.you_own} />
          </div>
        )}
        {n.gate && (
          <div className="rounded-xl px-3.5 py-3" style={{ border: `1px solid ${COLOURS.amber}66`, background: `${COLOURS.amber}0F` }}>
            <p className="text-[12px] font-bold uppercase tracking-[0.18em] mb-1" style={{ color: COLOURS.amber }}>Gate</p>
            <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{n.gate}</p>
          </div>
        )}
        {n.checkpoint && (
          <div className="rounded-xl px-3.5 py-3 space-y-1.5" style={{ border: `1px solid ${PARCHMENT}44`, background: `${PARCHMENT}08` }}>
            <p className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: `${PARCHMENT}AA` }}>Checkpoint</p>
            <Row label="Artefact" text={n.checkpoint.artefact} />
            {Array.isArray(n.checkpoint.reviewer_checks) && n.checkpoint.reviewer_checks.length > 0 && (
              <>
                <p className="font-bold" style={{ ...bodyStyle, color: PARCHMENT }}>Reviewer checks:</p>
                <Bullets items={n.checkpoint.reviewer_checks} />
              </>
            )}
            <Row label="Common failure" text={n.checkpoint.common_failure} />
          </div>
        )}
        {Array.isArray(n.skill_atoms) && n.skill_atoms.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-bold" style={{ ...bodyStyle, color: PARCHMENT }}>Skills:</span>
            {n.skill_atoms.map((a, i) => <Chip key={i}>{String(a)}</Chip>)}
          </div>
        )}
        {Array.isArray(n.prerequisites) && n.prerequisites.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-bold" style={{ ...bodyStyle, color: PARCHMENT }}>Depends on:</span>
            {n.prerequisites.map((p, i) => <Chip key={i}>{String(p)}</Chip>)}
          </div>
        )}
        {n.panels?.useful_tools?.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-bold" style={{ ...bodyStyle, color: PARCHMENT }}>Useful tools:</span>
            {n.panels.useful_tools.map((t, i) => (
              <Chip key={i}>{t.name}{t.version ? ` ${t.version}` : ''}</Chip>
            ))}
          </div>
        )}
        {n.panels?.tips?.length > 0 && (
          <>
            <p className="font-bold" style={{ ...bodyStyle, color: PARCHMENT }}>Tips:</p>
            <Bullets items={n.panels.tips.map((t) => t.text || t)} />
          </>
        )}
        {n.panels?.extra_to_learn?.length > 0 && (
          <>
            <p className="font-bold" style={{ ...bodyStyle, color: PARCHMENT }}>Extra to learn:</p>
            <Bullets items={n.panels.extra_to_learn.map((t) => t.text || t)} />
          </>
        )}
        {dep.beginner && (
          <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>
            <span className="font-bold" style={{ color: PARCHMENT }}>[Beginner]</span> {dep.beginner}
          </p>
        )}
        {dep.mid && (
          <Disclosure label="[Mid] — go deeper" testId={`layer${layerNo}-mid`}>
            <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{dep.mid}</p>
          </Disclosure>
        )}
        {dep.deep && (
          <Disclosure label="[Deep] — full picture" testId={`layer${layerNo}-deep`}>
            <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{dep.deep}</p>
          </Disclosure>
        )}
        <SpotIt key={`${layerNo}-${idx}`} spot={dep.spot_it} layerNo={layerNo} />
        <Sources srcs={[...(n.src || []), ...(dep.src || [])]} resources={n.resources}
          basisList={basisList} evidenceMap={evidenceMap} testId={`layer${layerNo}-sources`} />
      </Card>

      <StepNav idx={idx} total={nodes.length} layerNo={layerNo} itemWord="Step"
        onPrev={() => setIdx(Math.max(0, idx - 1))}
        onNext={() => setIdx(Math.min(nodes.length - 1, idx + 1))} />
    </div>
  );
};

/* ══ LAYER 4 · AI DEEP DIVE — selectable levels ══ */
const Layer4 = ({ data, evidenceMap }) => {
  const levelKeys = Object.keys(data.levels || {});
  const startIdx = Math.max(0, levelKeys.indexOf(data.default_level));
  const [sel, setSel] = useState(startIdx);
  const lv = (data.levels || {})[levelKeys[sel]] || {};
  return (
    <div className="space-y-5">
      {data.bridge_2030 && (
        <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={{ color: `${PARCHMENT}DD`, fontSize: 16 }}>
          {data.bridge_2030}
        </p>
      )}
      <div className="flex flex-wrap gap-2" data-testid="layer4-levels">
        {levelKeys.map((k, i) => (
          <button key={k} type="button" onClick={() => setSel(i)}
            className="rounded-full px-4 py-2 text-[12px] font-bold uppercase tracking-wider transition-colors"
            style={{
              minHeight: 40,
              background: sel === i ? PARCHMENT : `${PARCHMENT}0D`,
              color: sel === i ? DEEP : `${PARCHMENT}CC`,
              border: `1px solid ${sel === i ? PARCHMENT : `${PARCHMENT}33`}`,
            }}
            data-testid={`layer4-level-${i}`}>
            Level {i + 1}
          </button>
        ))}
      </div>
      <Card>
        <p className="font-bold leading-snug break-words" style={{ color: PARCHMENT, fontSize: 16.5 }}>{lv.heading}</p>
        {lv.body && <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{lv.body}</p>}
        <Row label="Analogy" text={lv.analogy} />
        <Row label="Worked example" text={lv.worked_example} />
        <Row label="Failure mode" text={lv.failure_mode} />
        <Sources srcs={lv.src} evidenceMap={evidenceMap} testId="layer4-level-sources" />
      </Card>
      {Array.isArray(data.agent_stack) && data.agent_stack.length > 0 && (
        <Disclosure label="Agent stack" count={data.agent_stack.length} testId="layer4-agent-stack">
          {data.agent_stack.map((a, i) => (
            <div key={i} className="rounded-xl px-3.5 py-3 space-y-1" style={{ border: `1px solid ${PARCHMENT}22` }}>
              <p className="font-semibold break-words" style={{ ...bodyStyle, color: PARCHMENT }}>{a.name}</p>
              <Row label="Role in stack" text={a.role_in_stack} />
              <Row label="Status" text={a.status} />
              <Row label="Human check" text={a.human_check} />
              {(a.confidence || a.confidence_note) && (
                <p className="leading-relaxed break-words" style={dimStyle}>
                  {[a.confidence, a.confidence_note].filter(Boolean).join(' — ')}
                </p>
              )}
            </div>
          ))}
        </Disclosure>
      )}
      {Array.isArray(data.guardrails) && data.guardrails.length > 0 && (
        <Disclosure label="Guardrails" count={data.guardrails.length} testId="layer4-guardrails">
          <Bullets items={data.guardrails} />
        </Disclosure>
      )}
      {Array.isArray(data.honesty_notes) && data.honesty_notes.length > 0 && (
        <Disclosure label="Honesty notes" count={data.honesty_notes.length} testId="layer4-honesty">
          <Bullets items={data.honesty_notes} />
        </Disclosure>
      )}
      <Sources srcs={data.src} evidenceMap={evidenceMap} testId="layer4-sources" />
    </div>
  );
};

/* ══ LAYER 5 · WHO SIGNS THE WORK — interactive handoff sequence ══ */
const Layer5 = ({ data, evidenceMap }) => {
  const stations = data.stations || [];
  const [idx, setIdx] = useState(0);
  const st = stations[idx] || {};
  const ws = st.who_signs || {};
  const next = stations[idx + 1];
  const colourOf = (c) => COLOURS[c] || `${PARCHMENT}77`;
  return (
    <div className="space-y-5">
      {data.day_frame && (
        <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={{ color: `${PARCHMENT}DD`, fontSize: 16 }}>
          {data.day_frame}
        </p>
      )}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="layer5-stations">
        {stations.map((s2, i) => {
          const c = colourOf((s2.who_signs || {}).colour);
          return (
            <React.Fragment key={s2.n}>
              {i > 0 && <ArrowRight size={12} style={{ color: `${PARCHMENT}44` }} />}
              <button type="button" onClick={() => setIdx(i)}
                className="rounded-full font-bold text-[12px] transition-all"
                style={{
                  width: 34, height: 34,
                  background: idx === i ? c : 'transparent',
                  color: idx === i ? DEEP : c,
                  border: `2px solid ${c}`,
                }}
                data-testid={`layer5-station-${s2.n}`}>
                {s2.n}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <Card tint={`${colourOf(ws.colour)}66`}>
        <p className="font-bold leading-snug break-words" style={{ color: PARCHMENT, fontSize: 16.5 }}>
          Desk {st.n}. {st.station}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {ws.colour && <Chip colour={colourOf(ws.colour)}>{ws.colour}</Chip>}
          {ws.sign_off && <Chip>{ws.sign_off}</Chip>}
        </div>
        {st.what_happens && <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{st.what_happens}</p>}
        <Row label="Agent" text={st.agent} />
        <Row label="Passes on" text={st.artefact_out} />
        <Row label="Who signs" text={ws.label} />
        <Row label="Silently breaks" text={st.silent_break} />
        <Row label="Accountable" text={st.accountable} />
        {st.horizon && typeof st.horizon === 'object' && (
          <div className="space-y-1">
            {Object.entries(st.horizon).map(([yr, h]) => (
              <p key={yr} className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>
                <span className="font-bold" style={{ color: colourOf(h.colour) }}>{yr} ({h.colour}): </span>{h.note}
              </p>
            ))}
          </div>
        )}
        {Array.isArray(st.dial_shifts) && st.dial_shifts.length > 0 && (
          <>
            <p className="font-bold" style={{ ...bodyStyle, color: PARCHMENT }}>Dial shifts:</p>
            <Bullets items={st.dial_shifts.map((x) => (typeof x === 'string' ? x : JSON.stringify(x)))} />
          </>
        )}
        <Sources srcs={[...(st.src || []), ...(ws.basis_ref ? [ws.basis_ref] : [])]}
          evidenceMap={evidenceMap} testId="layer5-sources" />
        {next && (
          <p className="flex items-center gap-1.5 leading-relaxed" style={dimStyle} data-testid="layer5-handoff">
            Hands off to <ArrowRight size={12} /> Desk {next.n}. {next.station}
          </p>
        )}
      </Card>

      <StepNav idx={idx} total={stations.length} layerNo={5} itemWord="Desk"
        onPrev={() => setIdx(Math.max(0, idx - 1))}
        onNext={() => setIdx(Math.min(stations.length - 1, idx + 1))} />

      {(data.dial || data.colour_rule) && (
        <Disclosure label="The dial" count={data.dial ? Object.keys(data.dial).length : undefined} testId="layer5-dial">
          {data.dial_default && <Row label="Default" text={data.dial_default} />}
          {data.horizon_default && <Row label="Horizon" text={data.horizon_default} />}
          {data.dial && Object.entries(data.dial).map(([k, v]) => (
            <p key={k} className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>
              <span className="font-bold" style={{ color: PARCHMENT }}>{k}: </span>{String(v)}
            </p>
          ))}
          {data.colour_rule && <p className="leading-relaxed break-words" style={dimStyle}>{data.colour_rule}</p>}
        </Disclosure>
      )}
    </div>
  );
};

/* ══ LAYER 6 · CATCH THE AI'S MISTAKE — commit-then-reveal exercises ══ */
const anchorOf = (x) => x.anchor || (x.location || {}).anchor || '';
const lineOf = (x) => (x.location || {}).line;

const Layer6 = ({ data, evidenceMap }) => {
  const samples = data.samples || [];
  const [idx, setIdx] = useState(0);
  const [marks, setMarks] = useState({});      // findingId → true (defect) | false (looks correct)
  const [committed, setCommitted] = useState(false);
  const s = samples[idx] || {};
  const findings = useMemo(() => {
    const items = [
      ...(s.defects || []).map((d) => ({ id: d.defect_id, anchor: anchorOf(d), line: lineOf(d), isDefect: true, d })),
      ...(s.decoys || []).map((d) => ({ id: d.decoy_id, anchor: anchorOf(d), line: lineOf(d), isDefect: false, d })),
    ];
    // present in artefact/document order (by supplied line number where given)
    return items.sort((a, b) => (a.line ?? 9999) - (b.line ?? 9999) || String(a.id).localeCompare(String(b.id)));
  }, [s]);
  const ak = s.answer_key || {};
  const dwById = Object.fromEntries((ak.defect_walkthrough || []).map((w) => [w.defect_id, w]));
  const allMarked = findings.length > 0 && findings.every((f) => marks[f.id] !== undefined);
  const goTo = (i) => { setIdx(i); setMarks({}); setCommitted(false); };
  const refs = [
    ...(s.defects || []).map((d) => d.realism_ref),
    ...(s.decoys || []).map((d) => d.realism_ref),
    ...(s.src || []),
  ].filter(Boolean);

  return (
    <div className="space-y-5">
      {idx === 0 && !committed && data.unlock?.copy && (
        <p className="leading-relaxed break-words rounded-xl px-4 py-3" style={{ ...dimStyle, border: `1px dashed ${PARCHMENT}44` }}>
          {data.unlock.copy}
        </p>
      )}
      <Dots idx={idx} total={samples.length} />
      <Card>
        <div className="flex flex-wrap gap-1.5">
          <Chip>{s.sample_id}</Chip>
          {s.artefact_type && <Chip>{s.artefact_type}</Chip>}
        </div>
        {s.context && <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{s.context}</p>}
        <Row label="Produced by" text={s.produced_by} />
        {s.artefact?.body && (
          <pre className="rounded-xl px-4 py-3 whitespace-pre-wrap break-words [overflow-wrap:anywhere] overflow-x-hidden text-[12.5px] leading-relaxed"
            style={{ background: `${PARCHMENT}0A`, border: `1px solid ${PARCHMENT}22`, color: `${PARCHMENT}CC`, fontFamily: 'ui-monospace, monospace' }}
            data-testid="layer6-artefact">
            {s.artefact.body}
          </pre>
        )}

        <p className="font-bold pt-1" style={{ ...bodyStyle, color: PARCHMENT }}>
          Your call — mark each highlighted span:
        </p>
        <div className="space-y-3">
          {findings.map((f) => {
            const mark = marks[f.id];
            const right = committed && mark === f.isDefect;
            const wrong = committed && mark !== undefined && mark !== f.isDefect;
            return (
              <div key={f.id} className="rounded-xl px-3.5 py-3 space-y-2"
                style={{ border: `1px solid ${right ? COLOURS.green : wrong ? COLOURS.red : `${PARCHMENT}33`}`, background: `${PARCHMENT}06` }}>
                <p className="leading-relaxed break-words [overflow-wrap:anywhere] italic" style={bodyStyle}>
                  “{f.anchor}”
                </p>
                {!committed && (
                  <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={() => setMarks((p) => ({ ...p, [f.id]: true }))}
                      className="rounded-full px-4 py-2 text-[12.5px] font-bold uppercase tracking-wider"
                      style={{
                        minHeight: 40,
                        background: mark === true ? `${COLOURS.red}33` : 'transparent',
                        border: `1px solid ${mark === true ? COLOURS.red : `${PARCHMENT}44`}`,
                        color: mark === true ? COLOURS.red : `${PARCHMENT}AA`,
                      }}
                      data-testid={`layer6-mark-defect-${f.id}`}>
                      Defect
                    </button>
                    <button type="button" onClick={() => setMarks((p) => ({ ...p, [f.id]: false }))}
                      className="rounded-full px-4 py-2 text-[12.5px] font-bold uppercase tracking-wider"
                      style={{
                        minHeight: 40,
                        background: mark === false ? `${COLOURS.green}22` : 'transparent',
                        border: `1px solid ${mark === false ? COLOURS.green : `${PARCHMENT}44`}`,
                        color: mark === false ? COLOURS.green : `${PARCHMENT}AA`,
                      }}
                      data-testid={`layer6-mark-correct-${f.id}`}>
                      Looks correct
                    </button>
                  </div>
                )}
                {committed && (
                  <div className="space-y-1.5" data-testid={`layer6-verdict-${f.id}`}>
                    <p className="flex items-start gap-1.5 font-bold leading-snug break-words" style={{ color: f.isDefect ? COLOURS.red : COLOURS.green, fontSize: 14.5 }}>
                      {mark === f.isDefect ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> : <XCircle size={15} className="mt-0.5 shrink-0" />}
                      {f.isDefect
                        ? `DEFECT${(f.d.taxon || f.d.severity) ? ` [${[f.d.taxon, f.d.severity].filter(Boolean).join(' · ')}]` : ''}`
                        : 'DECOY (correct)'}
                    </p>
                    {f.isDefect ? (
                      <>
                        <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{f.d.why_it_matters}</p>
                        {dwById[f.id]?.how_you_would_spot_it && <Row label="How you would spot it" text={dwById[f.id].how_you_would_spot_it} />}
                        {dwById[f.id]?.what_it_costs && <Row label="What it costs" text={dwById[f.id].what_it_costs} />}
                      </>
                    ) : (
                      <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{f.d.why_it_is_correct}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!committed && (
          <div className="text-center pt-2">
            <Pill onClick={() => setCommitted(true)} disabled={!allMarked} testId="layer6-commit">
              {allMarked ? 'Commit my calls' : 'Mark every span first'}
            </Pill>
          </div>
        )}

        {committed && (
          <div className="space-y-2 pt-1" data-testid="layer6-debrief">
            {s.expected_action && <Row label="Expected action" text={s.expected_action} />}
            {ak.action_rationale && <p className="leading-relaxed break-words [overflow-wrap:anywhere]" style={bodyStyle}>{ak.action_rationale}</p>}
            {ak.if_you_missed_everything && (
              <p className="leading-relaxed break-words rounded-xl px-4 py-3" style={{ ...bodyStyle, borderLeft: `3px solid ${PARCHMENT}`, background: `${PARCHMENT}10` }}>
                {ak.if_you_missed_everything}
              </p>
            )}
            <Sources srcs={refs} evidenceMap={evidenceMap} testId="layer6-sources" />
          </div>
        )}
      </Card>

      <StepNav idx={idx} total={samples.length} layerNo={6} itemWord="Exercise"
        onPrev={() => goTo(Math.max(0, idx - 1))}
        onNext={() => goTo(Math.min(samples.length - 1, idx + 1))} />
    </div>
  );
};

const LAYER_BODIES = { 1: Layer1, 2: NodeStepLayer, 3: NodeStepLayer, 4: Layer4, 5: Layer5, 6: Layer6 };

/* ── overlay shell — same open/close mechanism as OkaRojuPlayer ── */
export const RoadmapLayerOverlay = ({ itemId, layerNo, heading, onClose }) => {
  const cacheKey = `${itemId}:${layerNo}`;
  const [layer, setLayer] = useState(LAYER_CACHE.get(cacheKey) || null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (LAYER_CACHE.has(cacheKey)) { setLayer(LAYER_CACHE.get(cacheKey)); return; }
    axios.get(`${API}/api/role-layers/${itemId}/${layerNo}`)
      .then((r) => { LAYER_CACHE.set(cacheKey, r.data); setLayer(r.data); })
      .catch((e) => {
        const code = e?.response?.status;
        setErr(code === 423 || code === 403
          ? 'This layer unlocks together with its preview.'
          : 'This layer is being added.');
      });
  }, [cacheKey, itemId, layerNo]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const Body = LAYER_BODIES[layerNo];
  const evidenceMap = useMemo(() => {
    const m = {};
    (layer?.evidence_refs || []).forEach((e) => { if (e.evidence_id) m[e.evidence_id] = e; });
    return m;
  }, [layer]);

  return (
    <div
      className="fixed inset-0 z-[95] flex flex-col"
      style={{ background: DEEP, fontFamily: '"Manrope", "Segoe UI", system-ui, sans-serif' }}
      data-testid={`layer-overlay-${layerNo}`}
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
        <span className="text-[12px] font-bold uppercase tracking-[0.3em] pl-1"
          style={{ color: `${PARCHMENT}77` }}>
          Roadmap
        </span>
        <button type="button" onClick={onClose}
          className="inline-flex items-center gap-1 rounded-full px-4 text-xs uppercase tracking-widest"
          style={{ color: `${PARCHMENT}88`, minHeight: 44 }}
          data-testid={`layer-exit-${layerNo}`}>
          Exit <X size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-16">
        <div className="w-full max-w-2xl mx-auto">
          <h1 className="italic mb-6 mt-2"
            style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(22px,4vw,32px)', lineHeight: 1.2 }}
            data-testid={`layer-title-${layerNo}`}>
            {heading}
          </h1>

          {err && (
            <div className="text-center py-16" data-testid={`layer-error-${layerNo}`}>
              <p style={{ color: PARCHMENT, fontSize: 16 }}>{err}</p>
              <div className="mt-6"><Pill onClick={onClose} testId={`layer-error-close-${layerNo}`}>Close</Pill></div>
            </div>
          )}

          {!err && !layer && (
            <p className="text-center text-sm py-16" style={{ color: `${PARCHMENT}88` }}>Opening the layer…</p>
          )}

          {layer && (
            <div data-testid={`layer-content-${layerNo}`}>
              {Body ? <Body data={layer.data || {}} layerNo={layerNo} evidenceMap={evidenceMap} /> : null}
              <div className="text-center mt-10">
                <Pill onClick={onClose} testId={`layer-done-${layerNo}`}>Back to the preview</Pill>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* Gateway card — same anatomy as LiveOneDayButton: dark rounded-3xl card,
   small-caps eyebrow, italic serif line, parchment pill. */
export const RoadmapLayerEntry = ({ itemId, layerNo, heading }) => {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const subjectOpen = Number(layerNo) === 1 && params.get('subject') === 'roadmap';
  const openLayer = () => {
    if (Number(layerNo) !== 1) { setOpen(true); return; }
    setParams((p) => { p.set('subject', 'roadmap'); return p; }, { replace: true });
  };
  const closeSubject = () => {
    setParams((p) => { ['subject', 'su-topic', 'su-reading', 'su-view'].forEach((k) => p.delete(k)); return p; }, { replace: true });
    requestAnimationFrame(() => document.querySelector('[data-testid="layer-entry-1"]')?.focus({ preventScroll: true }));
  };
  const dot = String(heading).indexOf('·');
  const eyebrow = dot > -1 ? String(heading).slice(0, dot).trim() : `LAYER ${layerNo}`;
  const rest = dot > -1 ? String(heading).slice(dot + 1).trim() : String(heading);
  return (
    <>
      <button
        type="button"
        onClick={openLayer}
        className="w-full rounded-3xl px-6 py-6 text-left transition-transform hover:scale-[1.01]"
        style={{ background: DEEP, minHeight: 44 }}
        data-testid={`layer-entry-${layerNo}`}
      >
        <span className="block text-[12px] font-bold uppercase tracking-[0.3em] mb-1.5" style={{ color: `${PARCHMENT}77` }}>
          {eyebrow}
        </span>
        <span className="flex items-center justify-between gap-3">
          <span className="italic" style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(18px,2.4vw,24px)' }}>
            {rest}
          </span>
          <span className="rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-widest shrink-0"
            style={{ background: PARCHMENT, color: DEEP }}>
            Open
          </span>
        </span>
      </button>
      {subjectOpen && <SubjectUnderstanding itemId={String(itemId)} onClose={closeSubject} />}
      {open && Number(layerNo) !== 1 && (
        <RoadmapLayerOverlay
          itemId={String(itemId)}
          layerNo={layerNo}
          heading={heading}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default RoadmapLayerEntry;
